import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button, Modal, Form, Offcanvas } from 'react-bootstrap';
import ReactPaginate from 'react-paginate';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import withPermission from '../../../utils/withPermission';
import Swal from 'sweetalert2';
import { saveAs } from "file-saver";

const backendUrl = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${backendUrl}/api/payroll-providers`;

const PayrollProviderDetails = () => {
    const [providers, setProviders] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState({ 
        name: '', 
        code: '', 
        description: '',
        is_active: true 
    });
    const [isEdit, setIsEdit] = useState(false);
    const [selectedProvider, setSelectedProvider] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageCount, setPageCount] = useState(1);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const debounceTimeoutRef = useRef(null);

    const WithEditPermission = withPermission(({ children }) => children, ['payroll-provider-edit']);
    const WithDeletePermission = withPermission(({ children }) => children, ['payroll-provider-delete']);
    const WithCreatePermission = withPermission(({ children }) => children, ['payroll-provider-create']);

    useEffect(() => {
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }

        debounceTimeoutRef.current = setTimeout(() => {
            fetchProviders(1);
        }, searchQuery ? 500 : 0);

        return () => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
        };
    }, [searchQuery]);

    const fetchProviders = async (page = 1) => {
        try {
            setLoading(true);
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const response = await axios.get(API_URL, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                params: {
                    page,
                    search: searchQuery,
                    per_page: 10
                },
            });

            if (response.data && response.data.data) {
                setProviders(response.data.data);
                setPageCount(response.data.last_page || 1);
                setCurrentPage(response.data.current_page || 1);
            }
        } catch (error) {
            console.error('Error fetching payroll providers:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to fetch payroll providers. Please try again.',
                timer: 3000
            });
        } finally {
            setLoading(false);
        }
    };

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
    };

    const handleAddProvider = () => {
        setFormData({ 
            name: '', 
            code: '', 
            description: '',
            is_active: true 
        });
        setIsEdit(false);
        setSelectedProvider(null);
        setShowModal(true);
        setErrors({});
    };

    const handleEditProvider = (provider) => {
        setFormData({
            name: provider.name || '',
            code: provider.code || '',
            description: provider.description || '',
            is_active: provider.is_active || true
        });
        setIsEdit(true);
        setSelectedProvider(provider);
        setShowModal(true);
        setErrors({});
    };

    const handleDelete = async (id) => {
        Swal.fire({
            title: "Are you sure?",
            text: "You won't be able to revert this!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, delete it!"
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const userInfo = JSON.parse(localStorage.getItem('user-info'));
                    const origin = localStorage.getItem('user-origin');
                    const token = userInfo?.token;
                    
                    await axios.delete(`${API_URL}/${id}`, {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'Tenant-Origin': origin,
                        },
                    });
                    
                    setProviders(providers.filter(provider => provider.id !== id));
                    
                    Swal.fire({
                        icon: "success",
                        title: "Deleted!",
                        text: "The payroll provider has been deleted.",
                        timer: 2000
                    });
                } catch (error) {
                    console.error('Error deleting payroll provider:', error);
                    
                    let errorMessage = "Failed to delete payroll provider.";
                    if (error.response?.data?.message) {
                        errorMessage = error.response.data.message;
                    } else if (error.response?.data?.errors) {
                        errorMessage = Object.values(error.response.data.errors).join(', ');
                    }
                    
                    Swal.fire({
                        icon: "error",
                        title: "Error!",
                        text: errorMessage,
                        timer: 3000
                    });
                }
            }
        });
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;

        setFormData((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));

        setErrors((prevErrors) => ({
            ...prevErrors,
            [name]: undefined,
        }));
    };

    const getStatusBadge = (status) => {
        if (status === true || status === 1) {
            return <span className="badge bg-success">Active</span>;
        } else {
            return <span className="badge bg-danger">Inactive</span>;
        }
    };

    const handlePageChange = ({ selected }) => {
        const newPage = selected + 1;
        setCurrentPage(newPage);
        fetchProviders(newPage);
    };

    useEffect(() => {
        if (!showModal) {
            setErrors({});
        }
    }, [showModal]);

    const validateForm = () => {
        const newErrors = {};
        if (!formData.name.trim()) newErrors.name = 'Name is required';
        if (!formData.code.trim()) newErrors.code = 'Code is required';
        return newErrors;
    };

    const Toast = Swal.mixin({
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        didOpen: (toast) => {
            toast.onmouseenter = Swal.stopTimer;
            toast.onmouseleave = Swal.resumeTimer;
        },
    });

    const handleSubmit = async () => {
        const validationErrors = validateForm();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        setLoading(true);
        try {
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            if (isEdit && selectedProvider) {
                const response = await axios.put(
                    `${API_URL}/${selectedProvider.id}`,
                    formData,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'Tenant-Origin': origin,
                        },
                    }
                );

                if (response.data && response.data.data) {
                    setProviders(providers.map(provider => 
                        provider.id === selectedProvider.id ? response.data.data : provider
                    ));
                    
                    Toast.fire({
                        icon: "success",
                        title: "Payroll provider updated successfully!"
                    });
                }
            } else {
                const response = await axios.post(API_URL, formData, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Tenant-Origin': origin,
                    },
                });

                if (response.data && response.data.data) {
                    setProviders([response.data.data, ...providers]);
                    
                    Toast.fire({
                        icon: "success",
                        title: "Payroll provider created successfully!"
                    });
                }
            }
            
            setShowModal(false);
            fetchProviders(currentPage); // Refresh the list
        } catch (error) {
            console.error('Error saving payroll provider:', error);
            
            let errorMessage = "Failed to save payroll provider.";
            if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.response?.data?.errors) {
                // Handle validation errors from backend
                const backendErrors = error.response.data.errors;
                const formattedErrors = {};
                
                Object.keys(backendErrors).forEach(key => {
                    formattedErrors[key] = backendErrors[key][0];
                });
                
                setErrors(formattedErrors);
                errorMessage = "Please check the form for errors.";
            }
            
            Toast.fire({
                icon: "error",
                title: errorMessage
            });
        } finally {
            setLoading(false);
        }
    };

    const handleExport = async () => {
        try {
            const userInfo = JSON.parse(localStorage.getItem("user-info"));
            const origin = localStorage.getItem("user-origin");
            const token = userInfo?.token;

            const response = await axios.get(`${backendUrl}/api/payroll-providers/export`, {
                responseType: "blob",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Tenant-Origin": origin,
                },
            });

            const blob = new Blob([response.data], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            saveAs(blob, "payroll-providers.xlsx");
            
            Toast.fire({ 
                icon: "success", 
                title: "Exported successfully!" 
            });
        } catch (error) {
            console.error("Export error:", error);
            Toast.fire({ 
                icon: "error", 
                title: "Export failed!" 
            });
        }
    };

    const handleImport = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append("file", file);

        try {
            const userInfo = JSON.parse(localStorage.getItem("user-info"));
            const origin = localStorage.getItem("user-origin");
            const token = userInfo?.token;

            await axios.post(`${backendUrl}/api/payroll-providers/import`, formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                    Authorization: `Bearer ${token}`,
                    "Tenant-Origin": origin,
                },
            });

            Toast.fire({ 
                icon: "success", 
                title: "Imported successfully!" 
            });
            
            // Refresh the list after import
            setTimeout(() => {
                fetchProviders(currentPage);
            }, 1000);
            
            e.target.value = "";
        } catch (error) {
            Toast.fire({
                icon: "error",
                title: error.response?.data?.error || "Import failed! Please check your file.",
            });
            e.target.value = "";
        }
    };

    // Initial fetch
    // useEffect(() => {
    //     fetchProviders();
    // }, []);

    return (
        <div className="main-content">
            <div className="page-content">
                <div className="container-fluid">
                    <div className="row w-100">
                        <div className="col-12">
                            <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                                <h4 className="mb-sm-0 font-size-18">Payroll Providers</h4>
                                <div className="page-title-right">
                                    <ol className="breadcrumb m-0">
                                        <li className="breadcrumb-item">Payroll</li>
                                        <li className="breadcrumb-item active">Providers</li>
                                    </ol>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <div className="row">
                        <div className="col-12">
                            <div className="card">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between mb-3">
                                        <div className="d-flex">
                                            <div className="search-box me-2 mb-2 d-inline-block">
                                                <div className="position-relative">
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        placeholder="Search providers..."
                                                        value={searchQuery}
                                                        onChange={handleSearchChange}
                                                    />
                                                    <i className="bx bx-search-alt search-icon" />
                                                </div>
                                            </div>
                                        </div>

                                        <div className="d-flex align-items-center gap-2">
                                            {/* Export Button */}
                                            <button
                                                type="button"
                                                className="btn btn-light border"
                                                onClick={handleExport}
                                                data-bs-toggle="tooltip"
                                                data-bs-placement="top"
                                                title="Export to Excel"
                                            >
                                                <i className="mdi mdi-file-excel text-success"></i>
                                            </button>

                                            {/* Import Button */}
                                            <input
                                                type="file"
                                                accept=".xlsx, .xls"
                                                id="importProvidersFile"
                                                onChange={handleImport}
                                                style={{ display: "none" }}
                                            />
                                            <label
                                                htmlFor="importProvidersFile"
                                                className="btn btn-light border mb-0"
                                                data-bs-toggle="tooltip"
                                                data-bs-placement="top"
                                                title="Import from Excel"
                                            >
                                                <i className="mdi mdi-upload text-primary"></i>
                                            </label>

                                            {/* Add Provider Button */}
                                            <WithCreatePermission>
                                                <button
                                                    type="button"
                                                    className="btn rounded-circle border d-flex align-items-center justify-content-center round-create-button"
                                                    onClick={handleAddProvider}
                                                    data-bs-toggle="tooltip"
                                                    data-bs-placement="top"
                                                    title="Add Payroll Provider"
                                                >
                                                    <i className="mdi mdi-plus"></i>
                                                </button>
                                            </WithCreatePermission>
                                        </div>
                                    </div>

                                        <>
                                            <table className="table align-middle table-nowrap table-check table-list">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th style={{ width: '20px' }} className="align-middle">
                                                            <div className="form-check font-size-16">
                                                                <input className="form-check-input" type="checkbox" id="checkAll" />
                                                                <label className="form-check-label" htmlFor="checkAll"></label>
                                                            </div>
                                                        </th>
                                                        <th className="align-middle">#</th>
                                                        <th className="align-middle">Name</th>
                                                        <th className="align-middle">Code</th>
                                                        <th className="align-middle">Description</th>
                                                        <th className="align-middle">Status</th>
                                                        <th className="align-middle">Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {Array.isArray(providers) && providers.length > 0 ? (
                                                        providers.map((provider, index) => (
                                                            <tr key={provider.id}>
                                                                <td>
                                                                    <div className="form-check font-size-16">
                                                                        <input
                                                                            className="form-check-input"
                                                                            type="checkbox"
                                                                            id={`providerCheck${index}`}
                                                                        />
                                                                        <label
                                                                            className="form-check-label"
                                                                            htmlFor={`providerCheck${index}`}
                                                                        ></label>
                                                                    </div>
                                                                </td>
                                                                <td>{(currentPage - 1) * 10 + index + 1}</td>
                                                                <td>{provider.name}</td>
                                                                <td>
                                                                    <code>{provider.code}</code>
                                                                </td>
                                                                <td>{provider.description || '-'}</td>
                                                                <td>{getStatusBadge(provider.is_active)}</td>
                                                                <td>
                                                                    <div className="dropdown">
                                                                        <i
                                                                            className="mdi mdi-dots-horizontal font-size-18"
                                                                            data-bs-toggle="dropdown"
                                                                            aria-expanded="false"
                                                                            style={{ cursor: 'pointer' }}
                                                                        ></i>
                                                                        <ul className="dropdown-menu dropdown-menu-end font-size-14">
                                                                            <li>
                                                                                <WithEditPermission>
                                                                                    <button 
                                                                                        className="dropdown-item" 
                                                                                        onClick={() => handleEditProvider(provider)}
                                                                                    >
                                                                                        <i className="mdi mdi-pencil-outline me-2"></i>
                                                                                        Edit
                                                                                    </button>
                                                                                </WithEditPermission>
                                                                            </li>
                                                                            <li>
                                                                                <WithDeletePermission>
                                                                                    <button 
                                                                                        className="dropdown-item text-danger" 
                                                                                        onClick={() => handleDelete(provider.id)}
                                                                                    >
                                                                                        <i className="mdi mdi-delete-outline me-2"></i>
                                                                                        Delete
                                                                                    </button>
                                                                                </WithDeletePermission>
                                                                            </li>
                                                                        </ul>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ))
                                                    ) : (
                                                        <tr>
                                                            <td colSpan="7" className="text-center py-4">
                                                                No payroll providers found.
                                                                {searchQuery && (
                                                                    <div className="mt-2">
                                                                        <small className="text-muted">
                                                                            Try a different search term or{" "}
                                                                            <button 
                                                                                className="btn btn-link p-0" 
                                                                                onClick={() => setSearchQuery('')}
                                                                            >
                                                                                clear search
                                                                            </button>
                                                                        </small>
                                                                    </div>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>

                                            {providers.length > 0 && pageCount > 1 && (
                                                <div className="d-flex justify-content-end pagination-sm">
                                                    <ReactPaginate
                                                        previousLabel={'<'}
                                                        nextLabel={'>'}
                                                        breakLabel={'...'}
                                                        pageCount={pageCount}
                                                        marginPagesDisplayed={2}
                                                        pageRangeDisplayed={3}
                                                        onPageChange={handlePageChange}
                                                        containerClassName={'pagination pagination-rounded justify-content-end mb-2'}
                                                        pageClassName={'page-item'}
                                                        pageLinkClassName={'page-link'}
                                                        previousClassName={'page-item previous'}
                                                        previousLinkClassName={'page-link'}
                                                        nextClassName={'page-item next'}
                                                        nextLinkClassName={'page-link'}
                                                        breakClassName={'page-item'}
                                                        breakLinkClassName={'page-link'}
                                                        activeClassName={'active'}
                                                        forcePage={currentPage - 1}
                                                    />
                                                </div>
                                            )}
                                        </>
                                 

                                    {/* Modal for Add/Edit Payroll Provider */}
                                    <Offcanvas 
                                        show={showModal} 
                                        onHide={() => setShowModal(false)} 
                                        placement='end' 
                                        style={{ width: '480px' }}
                                    >
                                        <Offcanvas.Header closeButton>
                                            <Offcanvas.Title>
                                                {isEdit ? 'Edit Payroll Provider' : 'Add Payroll Provider'}
                                            </Offcanvas.Title>
                                        </Offcanvas.Header>
                                        <hr style={{ margin: 0 }} /><br />

                                        <Offcanvas.Body>
                                            <Form>
                                                <Form.Group controlId="providerName" className="mb-3">
                                                    <Form.Label style={{ fontSize: '13px' }}>
                                                        Name <span className="text-danger">*</span>
                                                    </Form.Label>
                                                    <Form.Control 
                                                        type="text" 
                                                        name="name" 
                                                        style={{ fontSize: '13px' }} 
                                                        value={formData.name} 
                                                        onChange={handleInputChange}
                                                        className={`form-control ${errors?.name ? 'is-invalid' : ''}`}
                                                        placeholder="Enter provider name"
                                                    />
                                                    {errors.name && (
                                                        <div className="invalid-feedback" style={{ fontSize: '12px' }}>
                                                            {errors.name}
                                                        </div>
                                                    )}
                                                </Form.Group>

                                                <Form.Group controlId="providerCode" className="mb-3">
                                                    <Form.Label style={{ fontSize: '13px' }}>
                                                        Code <span className="text-danger">*</span>
                                                    </Form.Label>
                                                    <Form.Control 
                                                        type="text" 
                                                        name="code" 
                                                        style={{ fontSize: '13px' }} 
                                                        value={formData.code} 
                                                        onChange={handleInputChange}
                                                        className={`form-control ${errors?.code ? 'is-invalid' : ''}`}
                                                        placeholder="Enter unique code (e.g., BOB, WPS)"
                                                    />
                                                    {errors.code && (
                                                        <div className="invalid-feedback" style={{ fontSize: '12px' }}>
                                                            {errors.code}
                                                        </div>
                                                    )}
                                                    <Form.Text className="text-muted" style={{ fontSize: '12px' }}>
                                                        Unique identifier for the payroll provider
                                                    </Form.Text>
                                                </Form.Group>

                                                <Form.Group className="mb-3">
                                                    <Form.Label style={{ fontSize: '13px' }}>Description</Form.Label>
                                                    <Form.Control 
                                                        style={{ fontSize: '13px' }}
                                                        as="textarea"
                                                        rows={3}
                                                        name="description"
                                                        value={formData.description}
                                                        onChange={handleInputChange}
                                                        placeholder="Enter provider description (optional)"
                                                    />
                                                </Form.Group>

                                                <Form.Group controlId="providerStatus" className="mb-4">
                                                    <Form.Check
                                                        type="switch"
                                                        id="custom-switch"
                                                        label="Active"
                                                        name="is_active"
                                                        checked={formData.is_active}
                                                        onChange={handleInputChange}
                                                    />
                                                    <Form.Text className="text-muted" style={{ fontSize: '12px' }}>
                                                        Inactive providers won't appear in dropdowns
                                                    </Form.Text>
                                                </Form.Group>
                                            </Form>
                                        </Offcanvas.Body>
                                        
                                        <div className="p-3 d-flex justify-content-end border-top">
                                            <Button 
                                                variant="secondary" 
                                                className="me-2" 
                                                onClick={() => setShowModal(false)}
                                                disabled={loading}
                                            >
                                                Cancel
                                            </Button>
                                            <Button 
                                                variant="primary" 
                                                onClick={handleSubmit}
                                                disabled={loading}
                                            >
                                                {loading ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                        {isEdit ? 'Updating...' : 'Saving...'}
                                                    </>
                                                ) : (
                                                    isEdit ? 'Update' : 'Save'
                                                )}
                                            </Button>
                                        </div>
                                    </Offcanvas>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PayrollProviderDetails;